import {
  AGGREGATE_USER_FREQ_BY_GROUP,
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
  BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
  FIRST_EVENT_TIME_PROPERTY,
  FUNNELS_TIME_TO_CONVERSION,
  PAGE_DURATION,
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {segmentationToPropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import {getSelectedLabelforBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {
  FeatureRolloutName,
  FeatureRollouts,
  isCompanyProfileEnabled,
  isMetricLevelDataGroupsEnabled,
} from 'common/report/util/feature-sampling';
import {Segmentation} from 'common/types/reports/query';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricType} from 'common/types/reports/bookmark';
import {getMetricDataGroupId} from 'common/query-builder/query-builder/util/data-groups';

import {hasOnlyEventBasedMetrics} from '../util';
import {BehaviorShowClause, ShowClause} from '../models/show-clause';

export function getOtherFilterOptions({
  showClauses,
  hasGlobalDataGroup,
  insightsReportMode,
  multiAttributionProperty,
  currentProject,
}: {
  showClauses: ShowClause[];
  hasGlobalDataGroup: boolean;
  insightsReportMode: BookmarkType;
  multiAttributionProperty: Segmentation | null;
  currentProject: CurrentProject | null;
}): Array<PropertySectionItem<string>> {
  const hasPeopleQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.People,
  );
  const hasFunnelsQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.Funnel,
  );
  const hasEventsQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.Event,
  );

  let showPQA = false;
  if (isCompanyProfileEnabled(currentProject)) {
    if (isMetricLevelDataGroupsEnabled(currentProject)) {
      showPQA = showClauses.some((clause) => clause instanceof BehaviorShowClause && getMetricDataGroupId(clause));
    } else {
      showPQA = hasGlobalDataGroup;
    }
  }

  return [
    FIRST_EVENT_TIME_PROPERTY,
    ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(currentProject)
      ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
      : []),
    ...(multiAttributionProperty && multiAttributionProperty.behavior
      ? [
          {
            ...segmentationToPropertyItem(multiAttributionProperty),
            ...BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
            label: getSelectedLabelforBehavior(multiAttributionProperty.behavior),
          },
        ]
      : []),
    ...(!hasPeopleQuery ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),
    ...(hasFunnelsQuery
      ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
      : []),
    ...(hasEventsQuery && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),
    ...(showPQA ? [AGGREGATE_USER_FREQ_BY_GROUP] : []),
  ];
}

export function getOtherGroupByOptions({
  showClauses,
  hasGlobalDataGroup,
  insightsReportMode,
  currentProject,
  metricsResource,
  behaviorsResource,
}: {
  showClauses: ShowClause[];
  hasGlobalDataGroup: boolean;
  insightsReportMode: BookmarkType;
  currentProject: CurrentProject;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): Array<PropertySectionItem<string>> {
  const hasPeopleQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.People,
  );
  const hasFunnelsQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.Funnel,
  );
  const hasEventsQuery = showClauses.some(
    (clause) => clause instanceof BehaviorShowClause && clause.behavior?.type === MetricType.Event,
  );

  let showPQA = false;
  if (isCompanyProfileEnabled(currentProject)) {
    if (isMetricLevelDataGroupsEnabled(currentProject)) {
      showPQA = showClauses.some((clause) => clause instanceof BehaviorShowClause && getMetricDataGroupId(clause));
    } else {
      showPQA = hasGlobalDataGroup;
    }
  }

  return [
    FIRST_EVENT_TIME_PROPERTY,
    ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(currentProject)
      ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
      : []),
    ...(!hasPeopleQuery ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),
    ...(hasOnlyEventBasedMetrics({
      showClauses,
      metricsResource,
      behaviorsResource,
    })
      ? [BEHAVIOR_MULTI_ATTRIBUTION]
      : []),
    ...(hasFunnelsQuery
      ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
      : []),
    ...(hasEventsQuery && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),
    ...(showPQA ? [AGGREGATE_USER_FREQ_BY_GROUP] : []),
  ];
}

export function getOtherAggregationOptions({
  hasEventsQuery,
  insightsReportMode,
}: {
  hasEventsQuery: boolean;
  insightsReportMode: BookmarkType;
}): Array<PropertySectionItem<string>> {
  const otherAggregationOptions: Array<PropertySectionItem<string>> = [
    ...(hasEventsQuery && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),
  ];
  return otherAggregationOptions;
}
